import React, { createContext, useCallback, useContext, useState } from "react"
import { IconButton, Tooltip } from "@mui/material"
import LightbulbIcon from "@mui/icons-material/Lightbulb"
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import { connectToBluetooth } from "../utils/bluetooth"

const BoardConnectionContext = createContext({
  characteristic: null,
  connecting: false,
  connect: async () => null,
  disconnect: () => {},
})

/**
 * One Web Bluetooth connection to the kilter board, shared by every page.
 * The app bar's lightbulb is the connect/disconnect control; pages read
 * `characteristic` and write to the wall while it's non-null.
 */
export function BoardConnectionProvider({ children }) {
  const [characteristic, setCharacteristic] = useState(null)
  const [connecting, setConnecting] = useState(false)

  const connect = useCallback(async () => {
    if (characteristic || connecting) {
      return characteristic
    }
    setConnecting(true)
    try {
      const next = await connectToBluetooth()
      if (!next) {
        return null
      }
      next.service?.device?.addEventListener("gattserverdisconnected", () =>
        setCharacteristic(null)
      )
      setCharacteristic(next)
      return next
    } catch (err) {
      console.error("Bluetooth connect failed:", err)
      return null
    } finally {
      setConnecting(false)
    }
  }, [characteristic, connecting])

  const disconnect = useCallback(() => {
    characteristic?.service?.device?.gatt?.disconnect()
    setCharacteristic(null)
  }, [characteristic])

  return (
    <BoardConnectionContext.Provider
      value={{ characteristic, connecting, connect, disconnect }}
    >
      {children}
    </BoardConnectionContext.Provider>
  )
}

export const useBoardConnection = () => useContext(BoardConnectionContext)

/**
 * App-bar lightbulb: unlit while disconnected, glowing while connected,
 * pulsing while the chooser is open. Hidden in browsers without Web
 * Bluetooth, where connecting can never work.
 */
export function BoardConnectionIndicator() {
  const { characteristic, connecting, connect, disconnect } =
    useBoardConnection()

  if (typeof navigator !== "undefined" && !("bluetooth" in navigator)) {
    return null
  }

  const title = characteristic
    ? "Board connected — tap to disconnect"
    : connecting
    ? "Connecting to board…"
    : "Connect to board"

  return (
    <Tooltip title={title}>
      <IconButton
        onClick={characteristic ? disconnect : connect}
        sx={{
          color: characteristic ? "#ffb74d" : "text.secondary",
          ...(characteristic && {
            filter: "drop-shadow(0 0 6px rgba(255, 183, 77, 0.7))",
          }),
          ...(connecting && {
            animation: "bulbPulse 1.2s ease-in-out infinite",
            "@keyframes bulbPulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.35 },
            },
          }),
        }}
      >
        {characteristic ? <LightbulbIcon /> : <LightbulbOutlinedIcon />}
      </IconButton>
    </Tooltip>
  )
}
